<template>
  <div>
    <div
      v-if="isLoading && !request"
      class="spinner-full-window d-flex"
    >
      <b-spinner
        variant="secondary"
        class="spinner"
      />
    </div>
    <div
      v-else
      class="DetailView"
    >
      <div class="DetailView__wrapper">
        <div class="DetailView__main">
          <h2 class="h3">
            {{ $t("Detail.reference") }} {{ request.case_ref }}
          </h2>

          <Tags
            v-if="showTagsPanel"
            :record="request"
          />

          <Information :request="request" />
        <!-- <b-alert
          :show="!hasEqualCityAndMunicipality"
          variant="info"
        >
          De opgegeven gemeente <strong>{{ requestMunicipality }}</strong>
          is niet gelijk aan de behandelende gemeente
          <strong>{{ activeMunicipality }}</strong> ({{ municipalityCode }}).
        </b-alert> -->
        </div>

        <div class="DetailView__sidebar">
          <RealisationLink
            :request="request"
            :disabled="!hasValidCoordinates"
            class="realisation-link"
          />

          <Assessment
            v-if="canManageRequest"
            :request="request"
            :assessment-mode="assessmentMode"
            @assessmentMode="handleAssessmentModeChange"
          />

          <SummaryTable
            :request="request"
            @assessmentMode="handleAssessmentModeChange"
          />

          <MapRequest
            :uuid="uuid"
            @open="handleOpenModal"
            @activeMunicipality="handleActiveMunicipality"
          />
        </div>

        <TheComments
          :record="request"
          :type="RECORD_TYPE.REQUEST"
        />
      </div>

      <b-modal
        id="file-error"
        title="Foutmelding"
      >
        <p>{{ $t('Detail.messages.fileDoenloadError') }}</p>
        <ul>
          <li>{{ $t('Detail.messages.nonExistingFile') }}</li>
          <li>{{ $t('Detail.messages.nonSecureFile') }}</li>
        </ul>
        <template #modal-footer="{ ok }">
          <b-button
            size="sm"
            @click="ok()"
          >
            OK
          </b-button>
        </template>
      </b-modal>

      <MapRequestModal
        v-if="getMonitoringConfig.enabled"
        :uuid="uuid"
        :show="showModal"
        @close="handleCloseModal"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

import MapRequest from '@/components/map/MapRequest';
import Information from '@/components/request/Information';
import SummaryTable from '@/components/request/SummaryTable.vue';
import Assessment from '@/components/request/Assessment.vue';
import RealisationLink from '@/components/request/RealisationLink.vue';
import MapRequestModal from '@/components/map/MapRequestModal.vue';
import Tags from '@/components/Tags.vue';
import TheComments from '@/components/Comments';
import { isValidLatitude, isValidLongitude } from '@/services/validation'
import { getRequestByUuid } from '@/services/requests'

import { RECORD_TYPE } from '@/../shared/valueholders/recordTypes';

export default {
  name: 'Detail',
  components: {
    TheComments,
    MapRequest,
    Information,
    SummaryTable,
    Assessment,
    RealisationLink,
    MapRequestModal,
    Tags
  },
  data() {
    return {
      showModal: false,
      assessmentMode: false,
      municipalityCode: null,
      hasEqualCityAndMunicipality: true,
      requestMunicipality: null,
      activeMunicipality: null,
      request: null,
      isLoading: true
    };
  },
  computed: {
    ...mapGetters('requests', ['hasRequest', 'requestByUuid', 'getCurrentRequest']),
    ...mapGetters('user', ['canAccessRequests', 'canManageRequest']),
    ...mapGetters('tenant', ['getEnabledTags', 'getMonitoringConfig', 'getMunicipalityOptions']),

    // loaded() {
    //   return this.request !== null;
    // },
    uuid() {
      return this.$route.params.uuid;
    },
    // request() {
    //   return this.requestByUuid({ uuid: this.uuid });
    // },
    hasValidCoordinates () {
      const { lat, lng } = this.request?.coordinates
      return isValidLatitude(lat) && isValidLongitude(lng)
    },

    /**
     * Only show the tag panel if there are tags connected to the request
     * or if there are tags that can be connected to the request
     */
    showTagsPanel() {
      return this.canManageRequest && (this.getEnabledTags.length || this.request?.hasTags());
    }
  },

  watch: {
    getCurrentRequest: {
      deep: true,
      handler (request) {
        this.request = request
      }
    }
  },

  async created() {
    this.RECORD_TYPE = RECORD_TYPE;

    if (!this.canAccessRequests) {
    this.$router.push({ name: 'Dashboard' });
    }

    await this.fetchRequest()

    if (this.canManageRequest) {
      this.assessmentMode = this.request.status === 2
    }
    this.isLoading = false
  },
  methods: {
    ...mapMutations('requests', ['setLastOpenedRequest']),

    goBack() {
      this.$router.push({
        name: 'RequestList'
      });
    },
    async fetchRequest () {
      const token = await this.$auth.getTokenSilently();
      this.request = await getRequestByUuid({ token, uuid: this.uuid }).catch(err => console.log(err))
    },
    handleOpenModal() {
      this.showModal = true;
    },
    handleCloseModal() {
      this.showModal = false;
    },

    handleAssessmentModeChange({ mode }) {
      this.fetchRequest()
      this.assessmentMode = !!mode;
    },
    handleActiveMunicipality (code) {
      this.municipalityCode = code
      this.activeMunicipality = this.getMunicipalityOptions.find(mun => mun.value === code)?.text
      this.requestMunicipality = this.request.address.municipality || this.request.address.city
      this.hasEqualCityAndMunicipality = this.activeMunicipality.toLowerCase().trim() === this.requestMunicipality.toLowerCase().trim()
    }
  }
};
</script>

<style lang="scss">
.DetailView {
  max-width: 1200px;
  margin: 0 auto;
  text-align: left;
  padding: 18px 0 30px;

  .DetailView__wrapper {
    box-sizing: content-box;
    position: relative;
    display: grid;
    gap: 30px;
    grid-template-columns: 1fr auto;
  }

  .DetailView__main,
  .DetailView__sidebar {
    padding-top: 30px;
  }

  .DetailView__main {
    a {
      cursor: pointer;
    }
    h2 {
      margin-bottom: 30px;
    }
    table {
      th {
        width: 225px;
      }
      td:nth-child(3n) {
        text-align: right;
        min-width: 40px;
      }
    }
    // table:nth-of-type(2) {
    //   th {
    //     width: 80%;
    //   }
    // }
  }

  .DetailView__sidebar {
    position: sticky;
    top: 0;
    align-self: start;

    width: 400px;

    select,
    input {
      width: 400px;
    }
  }

  .status:not(:last-child) {
    margin-right: 5px;
  }
  .realisation-link {
    margin-bottom: 1em;
  }
}
</style>
